import React from 'react';
import { styled } from 'styled-components';
import { TimespanUnit } from '@debbie/common/dist/index.js';
import { CaseStatus, convertUserToContact, } from '@debbie/common/dist/cases/index.js';
import { getContactPerson, } from '@debbie/common/dist/customers/index.js';
import { UpdateState, } from '@debbie/common/dist/updates/index.js';
import { PathType, TimingType, isPossibleTask, isPossiblePath, TodoType, RequirementType, PathWarning, } from '@debbie/common/dist/workflows/index.js';
import THEME from '../../theme.js';
import UpdateType from './UpdateType.js';
import { InteractionType, } from '@debbie/common/dist/interactions/index.js';
export var NodeTypes;
(function (NodeTypes) {
    NodeTypes["StartingPoint"] = "STARTING_POINT";
})(NodeTypes || (NodeTypes = {}));
const fromTaskToNode = (task, paths, updateTypesMap, onDelete, caseContext, workflowId) => {
    const _updateType = updateTypesMap[task.updateTypeId];
    if (!_updateType)
        throw `Could not find update type (${task.updateTypeId}) for task`;
    const nodePaths = paths.filter((path) => path.target === task.id);
    const _isPossibleTask = caseContext &&
        (isPossibleTask(task, {
            isCompany: caseContext.singleCase.isCompany,
            contact: convertUserToContact(getContactPerson(caseContext.customer)),
            updates: caseContext.updates,
            caseGroupId: caseContext.singleCase.caseGroup?.id || null,
            caseId: caseContext.singleCase.caseId,
            caseVouchers: caseContext.caseVouchers,
            creditorId: caseContext.singleCase.creditorId,
            hasFoundation: caseContext.hasFoundation,
            foundationTypes: caseContext.foundationTypes,
            customerTags: caseContext.customer.tags,
            interactions: caseContext.interactions,
        }, nodePaths, [PathType.Normal]) ||
            (!task.startingPoint &&
                nodePaths.filter((path) => path.type === PathType.Normal).length === 0));
    const executedUpdate = caseContext &&
        caseContext.updates.find((update) => update.state === UpdateState.Executed && update.taskId === task?.id);
    return {
        id: task.id,
        position: task.position,
        selectable: caseContext ? _isPossibleTask || !!executedUpdate : true,
        data: {
            updateTypeId: _updateType.updateTypeId,
            pauseAtTiming: task.pauseAtTiming,
            label: (React.createElement(UpdateType, { updateType: _updateType, task: task, onDelete: onDelete, caseContext: caseContext, isPossibleTask: _isPossibleTask, workflowId: workflowId })),
            alternativePaths: nodePaths.filter((path) => [PathType.ForMigration, PathType.DeprecatedTask].includes(path.type)),
        },
        className: 'task-node',
    };
};
const getNodesFromWorkflow = (workflow, updateTypesMap, onDelete, caseContext, workflowId) => {
    return workflow.tasks.map((task) => {
        return fromTaskToNode(task, workflow.paths, updateTypesMap, onDelete, caseContext, workflowId);
    });
};
const getTasksFromNodes = (nodes, edges) => {
    const toTasks = nodes
        .map((node) => {
        const edgesToNode = edges.filter((edge) => edge.target === node.id);
        const startingPointEdge = edgesToNode.find((edge) => edge.source === NodeTypes.StartingPoint);
        return {
            id: node.id,
            position: node.position,
            updateTypeId: node.data.updateTypeId,
            pauseAtTiming: node.data.pauseAtTiming,
            startingPoint: !!startingPointEdge,
            startingPointRequirements: startingPointEdge && startingPointEdge.data.requirements,
            startingPointPriority: startingPointEdge && startingPointEdge.data.priority,
            startingPointManual: startingPointEdge && startingPointEdge.data.manual,
        };
    })
        .filter((task) => !Object.values(NodeTypes).includes(task.id));
    // Remove duplicates - there have been a bug where the same task was added multiple times. Please remove later
    return toTasks.filter((path, index) => toTasks.findIndex((p) => p.id === path.id) === index);
};
const getPathsFromEdges = (edges, nodes) => {
    const toPaths = edges
        .map((edge) => {
        return {
            id: edge.id,
            source: edge.source,
            target: edge.target,
            priority: edge.data.priority,
            manual: edge.data.manual,
            type: edge.data.type,
            requirements: edge.data.requirements,
            timing: edge.data.timing,
        };
    })
        .filter((path) => !Object.values(NodeTypes).includes(path.target) &&
        !Object.values(NodeTypes).includes(path.source))
        .concat(nodes.reduce((acc, node) => {
        acc = acc.concat(node.data.alternativePaths || []);
        return acc;
    }, []));
    // Remove duplicates - there have been a bug where the same task was added multiple times. Please remove later
    return toPaths.filter((path, index) => toPaths.findIndex((p) => p.id === path.id) === index);
};
const getEdgeFromPath = (path, tasks, updateTypesMap, caseContext) => {
    const targetTask = path.source == NodeTypes.StartingPoint &&
        tasks.find((task) => task.id === path.target);
    const _isPossiblePath = caseContext &&
        isPossiblePath({
            ...path,
        }, {
            contact: convertUserToContact(getContactPerson(caseContext.customer)),
            isCompany: caseContext.singleCase.isCompany,
            updates: caseContext.updates,
            caseVouchers: caseContext.caseVouchers,
            caseGroupId: caseContext.singleCase.caseGroup?.id || null,
            caseId: caseContext.singleCase.caseId,
            creditorId: caseContext.singleCase.creditorId,
            hasFoundation: caseContext.hasFoundation,
            foundationTypes: caseContext.foundationTypes,
            customerTags: caseContext.customer.tags,
            interactions: caseContext.interactions,
        });
    const requirements = path.requirements || [];
    let warnings = [];
    if (!caseContext) {
        const task = tasks.find((task) => task.id === path.target);
        const updateType = updateTypesMap && task && updateTypesMap[task.updateTypeId];
        if (task && updateType) {
            warnings = getPathWarnings(updateType, requirements);
        }
    }
    return {
        id: path.id,
        source: path.source,
        target: path.target,
        animated: caseContext ? _isPossiblePath : true,
        type: 'custom',
        data: {
            caseContext,
            priority: typeof path.priority === 'number' ? path.priority : 1,
            requirements,
            warnings,
            manual: path.source == NodeTypes.StartingPoint
                ? targetTask && targetTask.startingPoint
                    ? targetTask.startingPointManual
                    : false
                : path.manual,
            timing: path.timing,
            type: path.type,
        },
    };
};
const getEdgesFromWorkflow = (workflow, updateTypesMap, caseContext) => {
    return workflow.paths
        .filter((path) => ![PathType.DeprecatedTask, PathType.ForMigration].includes(path.type))
        .map((path) => getEdgeFromPath(path, workflow.tasks, updateTypesMap, caseContext))
        .concat(workflow.tasks
        .filter((task) => task.startingPoint)
        .map((task) => getEdgeFromPath({
        id: `${task.id}-${NodeTypes.StartingPoint}`,
        target: task.id,
        source: NodeTypes.StartingPoint,
        manual: false,
        priority: typeof task.startingPointPriority === 'number'
            ? task.startingPointPriority
            : 1,
        requirements: task.startingPointRequirements || [],
        type: PathType.Normal,
        timing: {
            type: TimingType.Timespan,
            details: {
                timespan: [0, TimespanUnit.Days],
            },
        },
    }, workflow.tasks, updateTypesMap, caseContext)));
};
const getStartingPointNode = () => {
    return {
        id: NodeTypes.StartingPoint,
        position: { x: 500, y: 120 },
        draggable: false,
        selectable: false,
        data: {
            label: (React.createElement(StartingPoint, null,
                React.createElement("div", { className: "circle pulse" }))),
        },
        type: 'input',
        className: 'task-node starting-point',
    };
};
const getDefaultRequirements = (updateType) => {
    let requirements = [];
    if (!updateType) {
        return requirements;
    }
    if (updateType.procedure?.find((todo) => todo.type === TodoType.CreateInteraction &&
        todo.details.type === InteractionType.Eboks)) {
        requirements.push({
            id: crypto.randomUUID(),
            type: RequirementType.ReadyForEboks,
            details: {},
        });
    }
    if (updateType.procedure?.find((todo) => todo.type === TodoType.CreateInteraction &&
        todo.details.type === InteractionType.Letter)) {
        requirements.push({
            id: crypto.randomUUID(),
            type: RequirementType.HasValidAddress,
            details: {},
        });
    }
    if (updateType.procedure?.find((todo) => todo.type === TodoType.CreateInteraction &&
        todo.details.type === InteractionType.Email)) {
        requirements.push({
            id: crypto.randomUUID(),
            type: RequirementType.HasValidEmail,
            details: {},
        });
    }
    return requirements;
};
const getPathWarnings = (updateType, requirements) => {
    const warnings = [];
    // Ready for e-Boks requirement
    if (!requirements.find((requirement) => requirement.type === RequirementType.ReadyForEboks) &&
        updateType.procedure?.find((todo) => todo.type === TodoType.CreateInteraction &&
            todo.details.type === InteractionType.Eboks)) {
        warnings.push(PathWarning.ReadyForEboksRequirementRecommended);
    }
    // Valid e-mail requirement
    if (!requirements.find((requirement) => requirement.type === RequirementType.HasValidEmail) &&
        updateType.procedure?.find((todo) => todo.type === TodoType.CreateInteraction &&
            todo.details.type === InteractionType.Email)) {
        warnings.push(PathWarning.ValidEmailRequirementRecommended);
    }
    // Valid address requirement
    if (!requirements.find((requirement) => requirement.type === RequirementType.HasValidAddress) &&
        updateType.procedure?.find((todo) => todo.type === TodoType.CreateInteraction &&
            todo.details.type === InteractionType.Letter)) {
        warnings.push(PathWarning.ValidAddressRequirementRecommended);
    }
    return warnings;
};
export default {
    getEdgesFromWorkflow,
    getNodesFromWorkflow,
    fromTaskToNode,
    getStartingPointNode,
    getEdgeFromPath,
    getTasksFromNodes,
    getPathsFromEdges,
    getDefaultRequirements,
    getPathWarnings,
};
const StartingPoint = styled.div `
  background-color: ${THEME.STATUS_COLORS[CaseStatus.VERIFIED]};
  color: ${THEME.COLOR_SECONDARY};
  padding: 20px;
  margin: -16px;
  border-radius: 30px;
  font-size: 1.2em;
  font-weight: 900;
  text-transform: uppercase;
  overflow: hidden;

  .circle {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    box-shadow: 0px 0px 1px 1px #0000001a;
    background: white;
    margin: 0 3px;
  }

  .pulse {
    animation: pulse-animation 2s infinite;
  }

  @keyframes pulse-animation {
    0% {
      box-shadow: 0 0 0 0px rgba(255, 255, 255, 0.2);
    }
    100% {
      box-shadow: 0 0 0 30px rgba(255, 255, 255, 0);
    }
  }
`;
//# sourceMappingURL=helpers.js.map